import { cn } from '@lib/utils';
import { AlertCircle, Eye, EyeOff, type LucideIcon } from 'lucide-react';
import {
  forwardRef,
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';


export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  leftIcon?: LucideIcon;
  rightIcon?: LucideIcon;
  containerClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    hint,
    error,
    leftIcon: LeftIcon,
    rightIcon: RightIcon,
    type = 'text',
    id,
    className,
    containerClassName,
    disabled,
    ...rest
  },
  ref,
) {
  const reactId = useId();
  const inputId = id ?? reactId;
  const isPassword = type === 'password';
  const [revealPw, setRevealPw] = useState(false);
  const resolvedType = isPassword && revealPw ? 'text' : type;

  const hasError = Boolean(error);

  return (
    <div className={cn('flex w-full flex-col gap-1.5', containerClassName)}>
      {label ? (
        <label
          htmlFor={inputId}
          className="text-label text-apple-label-secondary"
        >
          {label}
        </label>
      ) : null}
      <div
        className={cn(
          'group relative flex items-center rounded-lg transition-all duration-200 ease-apple',
          hasError
            ? 'bg-[#FFF1F2] ring-3 ring-[#FDA4AF]/40'
            : 'bg-apple-gray-100 focus-within:bg-white focus-within:ring-3 focus-within:ring-link/20',
          disabled && 'opacity-60',
        )}
      >
        {LeftIcon ? (
          <LeftIcon
            size={16}
            strokeWidth={1.5}
            className="ml-3.5 shrink-0 text-apple-gray-500"
          />
        ) : null}
        <input
          ref={ref}
          id={inputId}
          type={resolvedType}
          disabled={disabled}
          className={cn(
            'w-full bg-transparent text-[15px] text-apple-label placeholder:text-apple-placeholder outline-none',
            'px-4 py-3',
            LeftIcon && 'pl-2',
            (isPassword || RightIcon) && 'pr-2',
            className,
          )}
          aria-invalid={hasError}
          aria-describedby={hint || error ? `${inputId}-desc` : undefined}
          {...rest}
        />
        {isPassword ? (
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setRevealPw((v) => !v)}
            className="mr-3 inline-flex h-7 w-7 items-center justify-center rounded-md text-apple-gray-500 hover:bg-apple-gray-200 hover:text-apple-label-secondary transition"
            aria-label={revealPw ? 'Hide password' : 'Show password'}
          >
            {revealPw ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
          </button>
        ) : RightIcon ? (
          <RightIcon
            size={16}
            strokeWidth={1.5}
            className="mr-3.5 shrink-0 text-apple-gray-500"
          />
        ) : null}
      </div>
      {hasError ? (
        <p
          id={`${inputId}-desc`}
          className="flex items-center gap-1.5 text-[12px] font-medium text-rose-600"
        >
          <AlertCircle size={13} strokeWidth={1.5} />
          {error}
        </p>
      ) : hint ? (
        <p id={`${inputId}-desc`} className="text-[12px] text-apple-gray-500">
          {hint}
        </p>
      ) : null}
    </div>
  );
});
